import { Injectable } from '@angular/core';
import { Chart } from 'chart.js/auto';
import { EngineDataPoint } from '../models/engine-data';
import { SpeedUnits } from '../models/speed-units';
import { IUnit } from '../interfaces/unit';
import { TorqueUnits } from '../models/torque-units';
import { PowerUnits } from '../models/power-units';
import { AppSubscription } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class ChartService {

  constructor() {
    Chart.defaults.color = '#f3f3f3';
    Chart.defaults.scale.grid.color = '#333'
  }

  createShiftPointsGraph(shiftPoints: Record<number, number>, lowerRPM: number, upperRPM: number) {
    let spValues = Object.values(shiftPoints);
    let shiftPointsScales: any = {};

    let shiftPointsDatasets = [];

    for (let i = 0; i < spValues.length; i++) {

      let y1 = i == 0 ? 0 : lowerRPM;
      let x1 = i > 0 ? spValues[i - 1] : 0;

      shiftPointsDatasets.push({
        data: [{ y: y1, x: x1 }, { y: upperRPM, x: spValues[i] }]
      });
      shiftPointsScales[`x${i}`] = {
        type: 'linear',
        display: false,
      };
    }

    let speedLabels = [];
    let i = 0;
    for (; i < shiftPoints[spValues.length - 1]; i += 50) {
      speedLabels.push(i);
    }

    speedLabels.push(i + 50);

    let spChart = new Chart('shiftPointsChart', {
      type: 'line',
      data: {
        labels: speedLabels,
        datasets: shiftPointsDatasets
      },
      options: {
        maintainAspectRatio: false,
        scales: shiftPointsScales,
        plugins: {
          legend: {
            display: false
          },
          title: {
            display: true,
            text: 'Shift Points'
          }
        }
      }
    });

    return spChart;
  }

  createDynoChart(engineData: Array<EngineDataPoint>, minRPM: number, maxRPM: number) {
    let rpmLabels = [];
    for (let i = minRPM; i <= maxRPM; i += 1000) {
      rpmLabels.push(i);
    }

    rpmLabels.push(maxRPM);

    let dynoChart = new Chart('dynoChart', {
      type: 'line',
      data: {
        labels: rpmLabels,
        datasets: [
          {
            label: 'Torque',
            data: engineData.map(ed => { return { x: ed.rpm, y: ed.torque } }),
            xAxisID: 'x1',
            yAxisID: 'y1'
          },
          {
            label: 'Power',
            data: engineData.map(ed => { return { x: ed.rpm, y: ed.power } }),
            xAxisID: 'x2',
            yAxisID: 'y2'
          }
        ]
      },
      options: {
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: 'Torque & Power'
          }
        },
        scales: {
          x1: {
            type: 'linear',
            min: minRPM,
            title: {
              display: true,
              text: 'RPM'
            },
            grid: {
              color: 'white',
            },
          },
          y1: {
            min: 0,
            title: {
              display: true,
              text: 'Torque'
            },
            grid: {
              color: 'white'
            }
          },
          x2: {
            type: 'linear',
            min: minRPM,
            display: false
          },
          y2: {
            position: 'right',
            title: {
              display: true,
              text: 'Power'
            },
            grid: {
              drawOnChartArea: false
            }
          }
        }
      }
    });

    return dynoChart;
  }

  createShiftPointsChartConfig(
    shiftPoints: Record<number, number>,
    lowerRPM: number, upperRPM: number,
    maxRPM: number, maxSpeed: number,
    maxSpeedAtMaxPower: number,
    speedUnits: IUnit,
    subscription: AppSubscription) {

    let spValues = Object.values(shiftPoints);
    let shiftPointsScales: any = {};

    let shiftPointsDatasets = [];

    maxSpeed = Math.round(maxSpeed);
    maxSpeedAtMaxPower = Math.round(maxSpeedAtMaxPower);

    if (speedUnits.id == SpeedUnits.kmh.id) {
      maxSpeed = SpeedUnits.speedToKmh(maxSpeed);
      maxSpeedAtMaxPower = SpeedUnits.speedToKmh(maxSpeedAtMaxPower);
    }

    for (let i = 0; i < spValues.length; i++) {
      if (speedUnits.id == SpeedUnits.kmh.id) {
        spValues[i] = SpeedUnits.speedToKmh(spValues[i]);
      }

      spValues[i] = Math.round(spValues[i]);

      let y1 = i == 0 ? 0 : lowerRPM;
      let x1 = i > 0 ? spValues[i - 1] : 0;
      let rpm = i == spValues.length - 1 ? maxRPM : upperRPM;

      shiftPointsDatasets.push({
        fill: false,
        data: [{ y: y1, x: x1 }, { y: rpm, x: spValues[i] }],
        label: `Gear: ${i + 1}`
      });

      shiftPointsScales[`x${i}`] = {
        type: 'linear',
        display: true,
      };
    }

    if (subscription.subscriptionLevel == 'Advanced' || subscription.subscriptionLevel == 'Trial') {
      shiftPointsDatasets.push({
        borderDash: [10, 5],
        fill: false,
        data: [
          { y: 0, x: maxSpeedAtMaxPower },
          { y: upperRPM, x: maxSpeedAtMaxPower }
        ],
        label: "Max Speed @ Max Power"
      });

      shiftPointsDatasets.push({
        borderDash: [10, 5],
        fill: false,
        data: [
          { y: upperRPM, x: 0 },
          { y: upperRPM, x: maxSpeedAtMaxPower },
        ],
        label: "Shift RPM"
      });

      shiftPointsDatasets.push({
        borderDash: [10, 5],
        fill: false,
        data: [
          { y: 0, x: maxSpeed },
          { y: maxRPM, x: maxSpeed }
        ],
        label: "Max Speed"
      })

      shiftPointsDatasets.push({
        borderDash: [10, 5],
        fill: false,
        data: [
          { y: maxRPM, x: 0 },
          { y: maxRPM, x: maxSpeed },
        ],
        label: "Max RPM"
      });
    }


    let speedLabels = [];
    let i = 0;
    for (; i < shiftPoints[spValues.length - 1]; i += 50) {
      speedLabels.push(i);
    }

    speedLabels.push(i);
    speedLabels.push(i + 50);


    let shiftPointChartConfig: any = {
      type: 'line',
      data: {
        labels: speedLabels,
        datasets: shiftPointsDatasets
      },
      options: {
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        scales: {
          x: {
            type: 'linear',
            min: 0,
            title: {
              display: true,
              text: `Speed (${speedUnits.display})`
            }
          },
          y: {
            min: 0,
            title: {
              display: true,
              text: "RPM"
            }
          },
        },
        plugins: {
          legend: {
            display: false
          },
          title: {
            display: true,
            text: 'Shift Points'
          },
          tooltip: {
            mode: 'dataset',
            intersect: false,
            callbacks: {
              label: (context: any): any => {
                //console.log(context);
                if (context.label == '0') {
                  return null
                }

                let datasetLabel = context.dataset.label.toLowerCase();

                if (context.dataset.label.toLowerCase().startsWith('gear')) {
                  return `${context.label} ${speedUnits.display}`;
                }

                if (datasetLabel.startsWith('max rpm') || datasetLabel.startsWith('shift rpm')) {
                  return Math.round(context.parsed.y);
                }

                if (datasetLabel.startsWith('max speed')) {
                  if (context.parsed.y == 0) {
                    return null;
                  }

                  return `${context.label} ${speedUnits.display}`;
                }

                return context.label;
              }
            }
          }
        }
      }
    };

    return shiftPointChartConfig;
  }


  createDynoChartConfig(engineData: Array<EngineDataPoint>, minRPM: number, maxRPM: number, torqueUnits: IUnit, powerUnits: IUnit) {
    let rpmLabels = [];

    for (let i = minRPM; i <= maxRPM; i += 1000) {
      rpmLabels.push(i);
    }

    rpmLabels.push(maxRPM);

    let clonedEngineData = engineData.map(ed => {
      let engineDataPoint: EngineDataPoint = {
        torque: torqueUnits.id != TorqueUnits.ftlb.id ? TorqueUnits.lbftToTorque(ed.torque, torqueUnits.display) : ed.torque,
        power: powerUnits.id != PowerUnits.hp.id ? PowerUnits.hpToPower(ed.power, powerUnits.display) : ed.power,
        rpm: ed.rpm
      }
      return engineDataPoint
    });
    

    let dynoChartConfig = {
      type: 'line',
      data: {
        labels: rpmLabels,
        datasets: [
          {
            label: 'Torque',
            data: clonedEngineData.map(ed => {
              return { x: ed.rpm, y: ed.torque }
            }),
            xAxisID: 'x1',
            yAxisID: 'y1'
          },
          {
            label: 'Power',
            data: clonedEngineData.map(ed => { return { x: ed.rpm, y: ed.power } }),
            xAxisID: 'x2',
            yAxisID: 'y2'
          }
        ]
      },
      options: {
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: 'Torque & Power'
          }
        },
        scales: {
          x1: {
            type: 'linear',
            min: minRPM,
            title: {
              display: true,
              text: 'RPM'
            }
          },
          y1: {
            min: 0,
            title: {
              display: true,
              text: `Torque (${torqueUnits.display})`
            }
          },
          x2: {
            type: 'linear',
            min: minRPM,
            display: false
          },
          y2: {
            position: 'right',
            title: {
              display: true,
              text: `Power (${powerUnits.display})`
            },
            grid: {
              drawOnChartArea: false
            }
          }
        }
      }
    };

    return dynoChartConfig;
  }

  createTractiveChartConfig(engineData: Array<EngineDataPoint>, gearRatios: Record<string, number>, finalGear: number, driveWheels: number, tireSize: number, speedUnits: IUnit) {
    let datasets: { label: string; fill: boolean; data: { y: number; x: number; }[]; }[] = [];

    Object.entries(gearRatios).forEach(gear => {
      let data = engineData.map(ed => {
        let convertedTorque = ed.torque * 1.3558;
        let tireRadius = (tireSize / 2) * 0.0254;
        let tractiveForce = ((convertedTorque * gear[1]) * finalGear) / driveWheels / tireRadius;
        let speed = ed.rpm * tireSize * 0.002975 / (finalGear * gear[1]);
        speed = speedUnits.display == SpeedUnits.kmh.display ? speed = SpeedUnits.speedToKmh(speed) : speed;
         
        return { y: tractiveForce, x: speed }
      });

      datasets.push({
        label: `gear ${gear[0]}`,
        fill: false,
        data: data
      });
    });

    let tractiveForceChartConfig: any = {
      type: 'line',
      data: {
        datasets: datasets,
      },
      options: {
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        scales: {
          x: {
            type: 'linear',
            min: 0,
            title: {
              display: true,
              text: `Speed (${speedUnits.display})`
            }
          },
          y: {
            min: 0,
            title: {
              display: true,
              text: ""
            }
          },
        },
        plugins: {
          legend: {
            display: false
          },
          title: {
            display: true,
            text: 'Tractive Force'
          },
          tooltip: {
            mode: 'nearest',
            intersect: false,
          }
        }
      }
    };

    return tractiveForceChartConfig;
  }

}
